import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, Eye, Pencil, PhoneCall, Loader2, AlertCircle,
  CheckCircle, Pause, Clock, RefreshCw, DownloadCloud, Layers,
} from 'lucide-react';
import { ivrAppsService, IvrApp } from 'api/services/ivrApps.service';
import { providersService, Provider } from 'api/services/providers.service';
import { useAuth } from 'hooks/useAuth';

/**
 * IVR Apps — the registry of configured flows, one per (operator_id, service_id).
 * Backed by cpass /telephony/ivr/apps.
 *
 * TWO IMPORTS LIVE ON THIS SCREEN AND THEY DO DIFFERENT THINGS. Confusing them
 * has already cost real time, so they are named for what they write.
 *
 *   "Import from runtime" (top right) writes the REGISTRY — the rows in this
 *   table. The backend says so in its own response: "App records only. Flow
 *   states are not copied."
 *
 *   "Rebuild from switch" (per row) writes the FLOW — ivr_flows.nodes, which is
 *   what the builder loads. This is the one to press when the editor opens
 *   blank, or after an importer fix: an import is a WRITE, and re-running it is
 *   the only thing that rebuilds that JSON. A fix cannot reach backwards into
 *   rows the old code already wrote.
 *
 * Pressing the first when you needed the second reports "Imported" and changes
 * nothing you can see, which is the worst possible outcome for somebody
 * debugging a blank editor.
 */
const IVRApps: React.FC = () => {
  const navigate = useNavigate();

  const [apps, setApps] = useState<IvrApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Rebuilding one flow from the switch.
  const { currentOrganization } = useAuth();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [rebuild, setRebuild] = useState<IvrApp | null>(null);
  const [providerId, setProviderId] = useState<string>('');
  const [preview, setPreview] = useState<any | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildError, setRebuildError] = useState<string | null>(null);

  useEffect(() => {
    // Only when somebody opens the panel — the provider list is not needed to
    // render the table, and this screen is opened far more often than a flow is
    // rebuilt.
    if (!rebuild || providers.length) return;
    providersService.list().then((r) => { if (r.success) setProviders(r.data || []); });
  }, [rebuild, providers.length]);

  const fetchApps = useCallback(async () => {
    setLoading(true); setError(null);
    const res = await ivrAppsService.list();
    if (res.success) setApps(res.data || []); else setError(res.error || 'Failed to load IVR apps');
    setLoading(false);
  }, []);
  useEffect(() => { fetchApps(); }, [fetchApps]);

  const runImport = async () => {
    setImporting(true); setNotice(null); setError(null);
    const res = await ivrAppsService.importFromRuntime();
    setImporting(false);
    if (res.success && res.data) {
      setNotice(`Imported ${res.data.flows} flows — ${res.data.created} new, ${res.data.updated} updated.`);
      fetchApps();
    } else setError(res.error || 'Import failed');
  };

  /**
   * Preview, then apply — the order the backend is built around.
   *
   * apply=false reports what WOULD be written and writes nothing. It is not
   * ceremony: this rewrites the nodes the builder edits, and the preview is the
   * only chance to notice the wrong provider was picked before it lands.
   */
  const runRebuild = async (apply: boolean) => {
    if (!rebuild || !providerId) return;
    const organizationId = currentOrganization?.id;
    if (!organizationId || organizationId === 'synthetic-org') {
      setRebuildError('No organisation on this session, so there is nothing to import into.');
      return;
    }
    setRebuilding(true); setRebuildError(null);
    const res = await ivrAppsService.importFlow({
      providerId: Number(providerId),
      organizationId,
      operatorId: rebuild.operator_id,
      serviceId: rebuild.service_id,
      apply,
    });
    setRebuilding(false);
    if (!res.success) { setRebuildError(res.error || 'Could not re-import that flow'); return; }
    if (apply) {
      setPreview(null);
      setRebuild(null);
      setNotice(`Rebuilt ${rebuild.service_id} from the switch — reopen it in the builder to see the result.`);
      fetchApps();
    } else {
      setPreview(res.data);
    }
  };

  const handleDelete = async (app: IvrApp) => {
    if (!app.id || !window.confirm(`Remove app "${app.name || app.service_id}" from the registry? (does not delete its states)`)) return;
    setDeletingId(app.id);
    const res = await ivrAppsService.remove(app.id);
    setDeletingId(null);
    if (res.success) setApps(prev => prev.filter(a => a.id !== app.id));
    else alert(res.error || 'Delete failed');
  };

  const viewStates = (app: IvrApp) =>
    navigate(`/telephony/ivr/states?operatorId=${encodeURIComponent(app.operator_id)}&serviceId=${encodeURIComponent(app.service_id)}`);

  const editInBuilder = (app: IvrApp) =>
    navigate(`/telephony/ivr/create?operatorId=${encodeURIComponent(app.operator_id)}&serviceId=${encodeURIComponent(app.service_id)}`);

  const filtered = apps.filter(a => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q ||
      (a.name || '').toLowerCase().includes(q) ||
      (a.service_id || '').toLowerCase().includes(q) ||
      (a.operator_id || '').toLowerCase().includes(q) ||
      (a.business_name || '').toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || (a.status || '').toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusBadge = (status?: string) => {
    const s = (status || 'ACTIVE').toUpperCase();
    if (s === 'ACTIVE') return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" />Active</span>;
    if (s === 'INACTIVE') return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800"><Pause className="w-3 h-3" />Inactive</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3" />Draft</span>;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">IVR Apps</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configured IVR flows — one app per operator / service.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runImport} disabled={importing} className="inline-flex items-center gap-2 px-4 py-2 border border-indigo-200 text-indigo-700 dark:text-indigo-300 dark:border-gray-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-gray-700 disabled:opacity-50">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />} Import from runtime
          </button>
          <button onClick={() => navigate('/telephony/ivr/create')} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"><Plus className="w-4 h-4" />Create IVR</button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search by app, service, operator, or business..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value="all">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="draft">Draft</option>
        </select>
        <button onClick={fetchApps} className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title="Refresh"><RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>

      {notice && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{notice}</div>}
      {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3"><AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" /><p className="text-sm text-red-700">{error}</p></div>}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <PhoneCall className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{searchTerm || statusFilter !== 'all' ? 'No matching apps' : 'No IVR apps yet'}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{searchTerm || statusFilter !== 'all' ? 'Adjust your search or filter.' : 'Import the flows already configured in the runtime, or create a new IVR.'}</p>
          {!searchTerm && statusFilter === 'all' && (
            <button onClick={runImport} disabled={importing} className="tl-btn tl-btn-primary disabled:opacity-50">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />} Import from runtime
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>{['App', 'Business', 'States', 'Directions', 'Status', 'Source', 'Actions'].map(h => (
                  <th key={h} className={`px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}</tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{app.name || app.service_id}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{app.operator_id} / {app.service_id}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{app.business_name || '—'}</td>
                    <td className="px-6 py-4"><span className="inline-flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300"><Layers className="w-3.5 h-3.5 text-gray-400" />{app.state_count ?? '—'}</span></td>
                    <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">{app.directions || '—'}{app.languages ? <span className="ml-1 text-gray-400">· {app.languages}</span> : ''}</td>
                    <td className="px-6 py-4">{statusBadge(app.status)}</td>
                    <td className="px-6 py-4 text-xs text-gray-400">{app.source || '—'}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => editInBuilder(app)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Edit in builder"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => viewStates(app)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded" title="View states"><Eye className="w-4 h-4" /></button>
                        {/* The import that writes the FLOW, not the registry.
                            Press this when the builder opens blank. */}
                        <button onClick={() => { setRebuild(app); setProviderId(''); setPreview(null); setRebuildError(null); }}
                                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                                title="Rebuild this flow's nodes from the switch">
                          <DownloadCloud className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(app)} disabled={deletingId === app.id} className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50" title="Remove from registry">
                          {deletingId === app.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rebuild && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
             onClick={() => setRebuild(null)} role="presentation">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-5"
               onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Rebuild {rebuild.name || rebuild.service_id} from the switch
            </h2>
            <p className="text-[12px] mt-1 text-gray-500 dark:text-gray-400">
              Reads {rebuild.operator_id} / {rebuild.service_id} from the runtime and rewrites the
              nodes the builder edits. This is the one to use when the editor opens blank — the
              other import only refreshes this table.
            </p>

            <label className="block text-[11px] font-medium uppercase tracking-wide mt-4 mb-1 text-gray-500 dark:text-gray-400">
              Which switch is running it?
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                    value={providerId} onChange={(e) => { setProviderId(e.target.value); setPreview(null); }}>
              <option value="">Choose a provider…</option>
              {providers.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}{p.region ? ` — ${p.region}` : ''}</option>
              ))}
            </select>
            {/* A flow lives on ONE switch, and picking the wrong one is not
                harmless: the preview is what catches it. */}
            <p className="text-[11px] mt-1 text-gray-500 dark:text-gray-400">
              Preview first — it writes nothing and shows what the switch actually holds.
            </p>

            {preview && (
              <pre className="mt-3 p-2 rounded bg-gray-50 dark:bg-gray-900/40 text-[11px] max-h-52
                              overflow-auto text-gray-700 dark:text-gray-300">
{JSON.stringify(preview, null, 2)}
              </pre>
            )}
            {rebuildError && (
              <p className="mt-3 text-[12px] text-red-600 dark:text-red-400">{rebuildError}</p>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
              <button className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300"
                      onClick={() => setRebuild(null)}>Cancel</button>
              <button className="px-3 py-1.5 text-sm border border-indigo-200 text-indigo-700
                                 dark:text-indigo-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
                      disabled={!providerId || rebuilding}
                      onClick={() => runRebuild(false)}>
                {rebuilding ? 'Working…' : 'Preview'}
              </button>
              {/* Only after a preview has come back: applying without looking is
                  exactly what the backend's dry-run default exists to prevent. */}
              <button className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg disabled:opacity-50"
                      disabled={!providerId || !preview || rebuilding}
                      onClick={() => runRebuild(true)}>
                {rebuilding ? 'Rebuilding…' : 'Rebuild'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && apps.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow"><p className="text-sm text-gray-500 dark:text-gray-400">Total Apps</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{apps.length}</p></div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow"><p className="text-sm text-gray-500 dark:text-gray-400">Active</p><p className="text-2xl font-bold text-green-600">{apps.filter(a => (a.status || '').toUpperCase() === 'ACTIVE').length}</p></div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow"><p className="text-sm text-gray-500 dark:text-gray-400">Total States</p><p className="text-2xl font-bold text-indigo-600">{apps.reduce((n, a) => n + (a.state_count || 0), 0)}</p></div>
        </div>
      )}
    </div>
  );
};

export default IVRApps;
